/**
 * KAN-925: переключатель вошедших аккаунтов (как в Google): шапка кабинета
 * (variant cabinet) и шапка приложения менеджера (variant mobile).
 * Кнопка показывает текущий аккаунт, меню: все вошедшие (имя, роль, кабинет),
 * "Добавить аккаунт", "Выйти" (только текущий) и "Выйти из всех".
 * Логика в useAccounts, здесь только отрисовка и открытие меню.
 * KAN-942: в кабинете пункт "Мобильная версия" (приложение /m/) для тех, у кого
 * оно есть (hasManagerApp); клик снимает выбор "кабинет на телефоне".
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, LogOut, Smartphone, UserPlus } from 'lucide-react';
import { useAccounts } from '@/hooks/useAccounts';
import { useCachedCurrentUser } from '@/hooks/useCurrentUser';
import { accountInitial, accountRoleLabel } from '@/lib/accountLabels';
import type { Account } from '@/lib/accounts';
import { forgetDesktopChoice, hasManagerApp, MOBILE_HOME } from '@/lib/mobileApp';
import { cn } from '@/lib/utils';

interface AccountSwitcherProps {
  variant?: 'cabinet' | 'mobile';
  className?: string;
}

function Avatar({ account, small }: { account: Account; small?: boolean }) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-blue-600 font-semibold text-white',
        small ? 'h-6 w-6 text-xs' : 'h-8 w-8 text-sm'
      )}
      aria-hidden="true"
    >
      {accountInitial(account.name, account.email)}
    </span>
  );
}

export function AccountSwitcher({ variant = 'cabinet', className }: AccountSwitcherProps) {
  const { accounts, active, switchTo, add, logout, logoutAll } = useAccounts();
  const currentUser = useCachedCurrentUser();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!active) return null;
  const mobile = variant === 'mobile';
  const showMobileVersion = !mobile && hasManagerApp(currentUser);

  const run = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <div ref={root} className={cn('relative shrink-0', className)}>
      <button
        type="button"
        className={cn(
          'flex min-w-0 items-center gap-1.5 rounded-full hover:bg-gray-100',
          mobile ? 'p-0.5' : 'py-1 pl-1 pr-2'
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Аккаунт: ${active.name}`}
        onClick={() => setOpen((v) => !v)}
        data-testid="account-switcher-trigger"
      >
        <Avatar account={active} small={mobile} />
        <span className={cn('max-w-[10rem] truncate text-sm text-gray-700', mobile ? 'sr-only' : 'hidden sm:inline')}>
          {active.name}
        </span>
        {!mobile && <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          data-testid="account-switcher-menu"
        >
          <ul>
            {accounts.map((account) => {
              const current = account.userId === active.userId;
              const meta = [accountRoleLabel(account.role), account.organizationName]
                .filter(Boolean)
                .join(' · ');
              return (
                <li key={account.userId}>
                  <button
                    type="button"
                    role="menuitem"
                    aria-current={current ? 'true' : undefined}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50',
                      current && 'bg-blue-50/60'
                    )}
                    onClick={run(() => switchTo(account.userId))}
                    data-testid={`account-switcher-item-${account.userId}`}
                  >
                    <Avatar account={account} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {account.name || account.email}
                      </span>
                      {meta && <span className="block truncate text-xs text-gray-500">{meta}</span>}
                    </span>
                    {current && <Check className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="my-1 border-t border-gray-100" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            onClick={run(add)}
            data-testid="account-switcher-add"
          >
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            Добавить аккаунт
          </button>
          {showMobileVersion && (
            <a
              href={MOBILE_HOME}
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => {
                forgetDesktopChoice();
                setOpen(false);
              }}
              data-testid="cabinet-mobile-version-link"
            >
              <Smartphone className="h-4 w-4" aria-hidden="true" />
              Мобильная версия
            </a>
          )}
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            onClick={run(logout)}
            data-testid="account-switcher-logout"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Выйти
          </button>
          {accounts.length > 1 && (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
              onClick={run(logoutAll)}
              data-testid="account-switcher-logout-all"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Выйти из всех
            </button>
          )}
        </div>
      )}
    </div>
  );
}
